import { Box, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { LiveKitPreviewConnector } from './livekit-preview-connector';

interface LiveKitPreviewModalProps {
  open: boolean;
  onClose: () => void;
  /** LiveKit room ID for connection (entityType:entityId:onAir) */
  livekitRoomId: string;
  /** Display title (e.g. "Stage player - John" or "Media player") */
  title: string;
  /** Optional: filter to these track SIDs */
  trackSids?: string[];
  /** Optional: only this publisher’s tracks (Mongo player id) */
  publisherPlayerId?: string;
  showCamera?: boolean;
  showScreenshare?: boolean;
}

export function LiveKitPreviewModal({
  open,
  onClose,
  livekitRoomId,
  title,
  trackSids = [],
  publisherPlayerId,
  showCamera = true,
  showScreenshare = true,
}: LiveKitPreviewModalProps) {
  const t = useTranslations();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          gap: 1,
          py: 1.5,
          px: 2,
        }}
      >
        <Box component="span" sx={{ typography: 'h6', pr: 1, flex: 1, minWidth: 0 }}>
          {t('media.preview' as TranslationKey) as string} - {title}
        </Box>
        <IconButton aria-label="close" onClick={onClose} sx={{ color: 'grey.500', flexShrink: 0 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          'p': 0,
          'overflow': 'hidden',
          '&.MuiDialogContent-root': { paddingTop: 0 },
        }}
      >
        {/*
          Responsive 16:9: height = width × 9/16 via padding-top 56.25%.
          Width is capped when the viewport is short so the frame never exceeds (90vh − header).
        */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: '100%',
            bgcolor: 'background.paper',
          }}
        >
          <Box
            sx={{
              // Cap width when the viewport is short so 16:9 height ≤ (90vh − header)
              width: 'min(100%, calc((90vh - 80px) * 16 / 9))',
              maxWidth: '100%',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                pt: '56.25%',
                bgcolor: 'black',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                {open && (
                  <LiveKitPreviewConnector
                    livekitRoomId={livekitRoomId}
                    mode="preview"
                    trackSids={trackSids}
                    publisherPlayerId={publisherPlayerId}
                    showCamera={showCamera}
                    showScreenshare={showScreenshare}
                    height="100%"
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
